import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import Steven, { ELEMENT_TYPES } from 'helpers/steven';
import Common from 'helpers/intl-common';
import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import DeletedAssets from './DeletedAssets';
import Tabs from 'components/shared/Tabs';
import ComingSoonImage from 'images/RickGetsToWork.png';
import TrashCanMessage from './TrashCanMessage';

// #region styled-components
const Heading = styled.h2`
  margin: 0 0 var(--cc_size_spacing_s);
  font-size: var(--cc_size_text_xl);
  font-weight: 800;
`;
const Intro = styled.p`
  display: flex;
  align-items: center;
  gap: var(--cc_size_spacing_xs);
  margin: 0 0 var(--cc_size_spacing_xs);
`;
const IntroIcon = styled.i`
  color: var(--cc_color_text_subtle);
  font-size: var(--cc_size_text_m);
`;
const IntroText = styled.span`
  color: var(--cc_color_text_subtle);
  font-size: var(--cc_size_text_s);
  line-height: calc(var(--cc_size_text_s) + 4px);
`;
// #endregion

const messages = defineMessages({
  emptyHeading: {
    id: 'Project.trashCan.emptyHeading',
    defaultMessage: 'Nothing to See Here',
    description: 'Project trash can empty heading',
  },
  emptyMessage: {
    id: 'Project.trashCan.emptyMessage',
    defaultMessage: 'No photos are currently in the Trash.',
    description: 'Project trash can empty state message',
  },
  errorMessage: {
    id: 'Project.trashCan.errorMessage',
    defaultMessage: 'There was an error loading the Trash. Probably our fault!',
    description: 'Project trash can error message',
  },
  introMessage: {
    id: 'Project.trashCan.introMessage',
    defaultMessage:
      "Items will be deleted 90 days after they're moved to Project Trash.",
    description: 'Intro message for Project Trash modal',
  },
  documentsComingSoon: {
    id: 'Project.trashCan.documentsComingSoon',
    defaultMessage:
      "We're working on getting your Documents in and out of the Trash.",
    description: 'Project trash can coming soon message for documents',
  },
  reportsComingSoon: {
    id: 'Project.trashCan.reportsComingSoon',
    defaultMessage:
      "We're working on getting your Reports in and out of the Trash.",
    description: 'Project trash can coming soon message for reports',
  },
});

const TrashCanModal = ({ onRequestClose, projectId }) => {
  const intl = useIntl();

  const handleTabSelected = (tabLabel) => {
    const action = `projects_trash_can_modal_${tabLabel}_tab_selected`;
    const trackingContent = `Projects Trash Can Modal Selected Tab: ${tabLabel}`;

    Steven.elementClicked({
      id: 'projects_trash_can_modal_tab',
      content: trackingContent,
      type: ELEMENT_TYPES.BUTTON,
      action
    });
  };

  return (
    <CCModal
      size="large"
      isOpen
      onRequestClose={onRequestClose}
      title={
        <>
          <Heading>
            <FormattedMessage
              id="Project.trashCan.title"
              defaultMessage="Project Trash"
              description="Modal title"
            />
          </Heading>
          <Intro>
            <IntroIcon aria-hidden="true" className="mdi mdi-delete-empty" />
            <IntroText>{intl.formatMessage(messages.introMessage)}</IntroText>
          </Intro>
        </>
      }
      label="Project Trash"
      bodyStyle={{
        padding: 0,
      }}
      headerStyle={{
        borderBottomWidth: 0,
        paddingBlockEnd: 0,
      }}
    >
      <Tabs
        id="project-trash-modal"
        onTabSelected={handleTabSelected}
        style={{
          backgroundColor: 'var(--cc_color_background_2)',
          borderRadius: 'var(--cc_size_border_radius_xl)',
        }}
        tabListStyle={{
          padding: '0 var(--cc_size_spacing_xl)',
          backgroundColor: 'var(--cc_color_background_1)',
        }}
        tabContentStyle={{
          padding: 0,
          position: 'relative',
        }}
      >
        <div label="Photos">
          <DeletedAssets projectId={projectId} />
        </div>
        <div label="Documents">
          <TrashCanMessage
            contentBefore={
              <img width="150" height="134" src={ComingSoonImage} alt="" />
            }
            heading={`${intl.formatMessage(Common.comingSoon)}...`}
            message={intl.formatMessage(messages.documentsComingSoon)}
          />
        </div>
        <div label="Reports">
          <TrashCanMessage
            contentBefore={
              <img width="150" height="134" src={ComingSoonImage} alt="" />
            }
            heading={`${intl.formatMessage(Common.comingSoon)}...`}
            message={intl.formatMessage(messages.reportsComingSoon)}
          />
        </div>
      </Tabs>
    </CCModal>
  );
};

TrashCanModal.propTypes = {
  onRequestClose: PropTypes.func,
  projectId: PropTypes.number,
};

export default TrashCanModal;
